import {ArrowDown, ArrowLeft, ArrowRight, ArrowUp, Check, FileAudio2, Images, Trash2, UploadCloud, X} from "lucide-react";
import {useEffect, useMemo, useState, type FormEvent} from "react";
import {useNavigate} from "react-router-dom";
import {createDraft, confirmUploaded, submitTask, uploadFile} from "../api";
import type {UploadTarget} from "@book-video-studio/contracts";

type UploadProgress = Record<string, number>;

const sizeText = (bytes: number): string => `${(bytes / 1024 / 1024).toFixed(1)} MB`;

const mediaMimeType = (file: File): string => {
  if (file.type) return file.type;
  return /\.(mp4|mov|m4v|webm|mpeg|mpg)$/iu.test(file.name) ? "video/mp4" : "audio/mpeg";
};

const ImagePreview = ({file}: {file: File}) => {
  const [source, setSource] = useState("");
  useEffect(() => {
    const objectUrl = URL.createObjectURL(file);
    setSource(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);
  return source ? <img src={source} alt="" /> : null;
};

export const NewTaskPage = () => {
  const navigate = useNavigate();
  const [taskName, setTaskName] = useState("");
  const [videoTitle, setVideoTitle] = useState("");
  const [videoSubtitle, setVideoSubtitle] = useState("");
  const [source, setSource] = useState<File | null>(null);
  const [images, setImages] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<UploadProgress>({});
  const ready = Boolean(source && images.length > 0 && !busy);
  const overall = useMemo(() => {
    const values = Object.values(progress);
    return values.length ? Math.round(values.reduce((sum, value) => sum + value, 0) / values.length) : 0;
  }, [progress]);

  const move = (index: number, direction: -1 | 1) => {
    const next = index + direction;
    if (next < 0 || next >= images.length) return;
    setImages((current) => {
      const copy = [...current];
      const first = copy[index];
      const second = copy[next];
      if (!first || !second) return current;
      copy[index] = second;
      copy[next] = first;
      return copy;
    });
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!source || images.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      const draft = await createDraft({
        workflow: "image-media-slideshow-v2",
        ...(taskName.trim() ? {title: taskName.trim()} : {}),
        source: {name: source.name, mimeType: mediaMimeType(source), sizeBytes: source.size},
        images: images.map((file) => ({name: file.name, mimeType: file.type || "image/jpeg", sizeBytes: file.size})),
        settings: {
          width: 1080,
          height: 1440,
          fps: 30,
          ...(videoTitle.trim() ? {title: videoTitle.trim()} : {}),
          ...(videoSubtitle.trim() ? {subtitle: videoSubtitle.trim()} : {}),
          transitionMs: 500,
          minimumSilenceMs: 350,
          minimumCaptionGapMs: 120,
          silenceToleranceMs: 650,
          maxImageHoldMs: 10000,
          recentImageExclusionCount: 3
        }
      });
      const fileFor = (target: UploadTarget): File => {
        if (target.kind === "audio" || target.kind === "video") return source;
        const image = images[target.sortIndex];
        if (target.kind === "image" && image) return image;
        throw new Error("上传文件与任务不匹配");
      };
      const queue = [...draft.uploads];
      const worker = async () => {
        while (queue.length) {
          const target = queue.shift();
          if (!target) return;
          const file = fileFor(target);
          setProgress((current) => ({...current, [target.assetId]: 0}));
          await uploadFile(target, file, (percent) => setProgress((current) => ({...current, [target.assetId]: percent})));
          await confirmUploaded(draft.task.id, target.assetId);
        }
      };
      await Promise.all([worker(), worker(), worker()]);
      await submitTask(draft.task.id);
      navigate(`/tasks/${draft.task.id}`, {replace: true});
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "创建任务失败");
      setBusy(false);
    }
  };

  return (
    <form className="page-grid create-form" onSubmit={(event) => void submit(event)}>
      <aside className="process-rail process-steps">
        <span>02</span><b>新建任务</b>
        <ol><li className="active">选择素材</li><li className={busy ? "active" : ""}>上传 COS</li><li>本地制作</li><li>成片回传</li></ol>
      </aside>
      <section className="page-content">
        <header className="page-heading"><div><p className="eyebrow">NEW PRODUCTION ORDER</p><h1>新建视频</h1></div><button className="icon-button" type="button" onClick={() => navigate(-1)} aria-label="返回"><X size={20} /></button></header>
        <div className="field-block">
          <label htmlFor="task-name"><span>任务名称</span><small>选填，方便以后查找</small></label>
          <input id="task-name" value={taskName} onChange={(event) => setTaskName(event.target.value)} maxLength={120} placeholder="例如：9月家风视频" disabled={busy} />
        </div>
        <div className="field-block">
          <label htmlFor="video-title"><span>视频标题</span><small>选填，显示在模板顶部</small></label>
          <input id="video-title" value={videoTitle} onChange={(event) => setVideoTitle(event.target.value)} maxLength={120} placeholder="例如：青色润古今" disabled={busy} />
        </div>
        <div className="field-block">
          <label htmlFor="video-subtitle"><span>视频副标题</span><small>选填，显示在标题下方</small></label>
          <input id="video-subtitle" value={videoSubtitle} onChange={(event) => setVideoSubtitle(event.target.value)} maxLength={120} placeholder="例如：一字藏天地，青色润古今" disabled={busy} />
        </div>
        <div className="asset-columns media-source-column">
          <label className={`file-drop ${source ? "has-file" : ""}`}>
            <FileAudio2 size={28} /><span><b>{source?.name || "选择音频或视频"}</b><small>{source ? sizeText(source.size) : "系统会提取旁白、自动转录、纠错并排版字幕"}</small></span>
            {source ? <Check size={20} /> : <UploadCloud size={20} />}
            <input type="file" accept="audio/*,video/*,.m4a,.mov,.m4v,.webm" onChange={(event) => setSource(event.target.files?.[0] ?? null)} disabled={busy} />
          </label>
        </div>
        <section className="image-section">
          <div className="section-heading"><div><Images size={22} /><span><b>轮播图片</b><small>至少 1 张；每张最多展示 10 秒，图片少时会自动复用</small></span></div><label className="button button-secondary"><UploadCloud size={18} />选择图片<input type="file" accept="image/jpeg,image/png,image/webp,image/avif" multiple onChange={(event) => setImages(Array.from(event.target.files ?? []))} disabled={busy} /></label></div>
          {images.length === 0 ? <div className="image-empty">还未选择图片</div> : (
            <ol className="image-order">
              {images.map((file, index) => (
                <li key={`${file.name}-${file.lastModified}-${index}`}>
                  <span className="image-index">{String(index + 1).padStart(2, "0")}</span>
                  <ImagePreview file={file} />
                  <span className="image-name"><b>{file.name}</b><small>{sizeText(file.size)}</small></span>
                  <span className="reorder-buttons">
                    <button type="button" onClick={() => move(index, -1)} disabled={busy || index === 0} aria-label="上移"><ArrowUp size={17} /></button>
                    <button type="button" onClick={() => move(index, 1)} disabled={busy || index === images.length - 1} aria-label="下移"><ArrowDown size={17} /></button>
                    <button type="button" onClick={() => setImages((current) => current.filter((_, itemIndex) => itemIndex !== index))} disabled={busy} aria-label="删除"><Trash2 size={17} /></button>
                  </span>
                </li>
              ))}
            </ol>
          )}
        </section>
        {busy ? <div className="upload-summary"><span>正在上传并核验素材</span><b>{overall}%</b><i><em style={{width: `${overall}%`}} /></i></div> : null}
        {error ? <p className="notice notice-error" role="alert">{error}</p> : null}
      </section>
      <footer className="mobile-action-bar"><button className="button button-ghost" type="button" onClick={() => navigate(-1)} disabled={busy}><ArrowLeft size={18} />取消</button><button className="button button-primary" type="submit" disabled={!ready}>{busy ? `上传中 ${overall}%` : "提交任务"}<ArrowRight size={18} /></button></footer>
    </form>
  );
};
